import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  message,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Typography,
  Select,
  DatePicker,
  Row,
  Col,
  Drawer,
  InputNumber
} from 'antd';
import { useSelector } from 'react-redux';
import useAuthHeader from '../../hooks/useAuthHeader';
import Container from '../../components/Container/Container';
import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import getAllDeposits from '../../api/admin/deposit/getAllDeposits';
import updateDepositStatus from '../../api/admin/deposit/updateDepositStatus';
import searchPartner from '../../api/partner/searchPartner';
import createDeposit from '../../api/admin/deposit/createDeposit';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const AdminDeposit = () => {
  const [loading, setLoading] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    dateRange: [dayjs().subtract(7, 'days'), dayjs()],
    partnerId: null,
    username: '',
    status: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0
  });
  const [createForm] = Form.useForm();
  const accessToken = useAuthHeader();

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      width: '15%',
    },
    {
      title: 'Đối tác',
      dataIndex: 'user',
      key: 'user',
      render: (user) => user?.username || '-',
      width: '15%',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount),
      align: 'right',
      width: '15%',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      width: '25%',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'statusCode',
      key: 'statusCode',
      render: (status) => {
        const statusConfig = {
          0: { color: 'gold', text: 'Đang xử lý' },
          1: { color: 'green', text: 'Thành công' },
          2: { color: 'red', text: 'Thất bại' },
        };
        const config = statusConfig[status] || { color: 'default', text: 'Không xác định' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
      width: '12%',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        record.statusCode === 0 && (
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleAction(record, 1)}
            >
              Duyệt
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={() => handleAction(record, 2)}
            >
              Từ chối
            </Button>
          </Space>
        )
      ),
    },
  ];

  const handleAction = (deposit, status) => {
    setSelectedDeposit({ 
      ...deposit, 
      newStatus: status,
      depositId: deposit.depositId 
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      setLoading(true);
      
      const response = await updateDepositStatus({
        depositId: selectedDeposit.depositId,
        status: selectedDeposit.newStatus,
      }, accessToken);

      if (response.success) {
        message.success('Cập nhật trạng thái thành công');
        setModalVisible(false);
        fetchDeposits(pagination.current);
      } else {
        message.error(response.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      message.error('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    fetchDeposits(newPagination.current);
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      dateRange: dates
    }));
    fetchDeposits(1, { ...filters, dateRange: dates });
  };

  const handlePartnerChange = (value) => {
    setFilters(prev => ({
      ...prev,
      partnerId: value
    }));
    fetchDeposits(1, { ...filters, partnerId: value });
  };

  const handleCreateDeposit = async (values) => {
    try {
      setLoading(true);
      const response = await createDeposit({
        userId: values.partnerId,
        amount: values.amount,
        note: values.note
      }, accessToken);

      if (response.success) {
        message.success('Tạo lệnh nạp tiền thành công');
        setDrawerVisible(false);
        createForm.resetFields();
        fetchDeposits(1);
      } else {
        message.error(response.message || 'Có lỗi xảy ra khi tạo lệnh nạp tiền');
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo lệnh nạp tiền');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await searchPartner(accessToken, searchQuery);
      if (response.success) {
        setPartners(response.data);
      }
    } catch (error) {
      message.error('Không thể tải danh sách đối tác');
    }
  };

  useEffect(() => {
    if (searchQuery) {
      fetchPartners();
    }
  }, [searchQuery]);

  const fetchDeposits = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: pagination.pageSize,
        search: {}
      };
      
      if (currentFilters.dateRange) {
        params.search.fromDate = currentFilters.dateRange[0].format('YYYY-MM-DD');
        params.search.toDate = currentFilters.dateRange[1].format('YYYY-MM-DD');
      }
      
      if (currentFilters.username) {
        params.username = currentFilters.username;
      }

      if (currentFilters.status !== null) {
        params.search.status = currentFilters.status;
      }

      const response = await getAllDeposits(accessToken, params);
      if (response.success) {
        const { data: { data, total, current_page, per_page } } = response;
        setDeposits(data);
        setPagination({
          current: current_page,
          pageSize: per_page,
          total: total
        });
      } else {
        message.error('Không thể tải danh sách yêu cầu nạp tiền');
      }
    } catch (error) {
      message.error('Không thể tải danh sách yêu cầu nạp tiền');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    setFilters(prev => ({
      ...prev,
      ...values
    }));
    fetchDeposits(1, { ...filters, ...values });
  };

  useEffect(() => {
    fetchDeposits(1, filters);
  }, []);

  return (
    <Container>
      <div className="p-6">
        <Card
          title="Danh sách yêu cầu nạp tiền"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setDrawerVisible(true)}
            >
              Tạo lệnh nạp tiền
            </Button>
          }
        >
          <Form layout="vertical" className="mb-4" onFinish={handleSearch}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Thời gian">
                  <RangePicker
                    style={{ width: '100%' }}
                    onChange={(dates) => {
                      setFilters(prev => ({
                        ...prev,
                        dateRange: dates
                      }));
                      fetchDeposits(1, { ...filters, dateRange: dates });
                    }}
                    placeholder={['Từ ngày', 'Đến ngày']}
                    value={filters.dateRange}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Trạng thái">
                  <Select
                    placeholder="Chọn trạng thái"
                    allowClear
                    onChange={(value) => {
                      setFilters(prev => ({
                        ...prev,
                        status: value
                      }));
                      fetchDeposits(1, { ...filters, status: value });
                    }}
                    value={filters.status}
                  >
                    <Select.Option value={0}>Đang xử lý</Select.Option>
                    <Select.Option value={1}>Thành công</Select.Option>
                    <Select.Option value={2}>Thất bại</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          
          <Table
            columns={columns}
            dataSource={deposits}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số ${total} bản ghi`,
              pageSizeOptions: ['10', '15', '30', '50']
            }}
            onChange={handleTableChange}
            bordered
            scroll={{ x: true }}
          />
        </Card>

        <Drawer
          title="Tạo lệnh nạp tiền"
          width={520}
          onClose={() => {
            setDrawerVisible(false);
            createForm.resetFields();
          }}
          open={drawerVisible}
          bodyStyle={{ paddingBottom: 80 }}
          extra={
            <Space>
              <Button onClick={() => {
                setDrawerVisible(false);
                createForm.resetFields();
              }}>
                Hủy
              </Button>
              <Button
                type="primary"
                loading={loading}
                onClick={() => createForm.submit()}
              >
                Tạo
              </Button>
            </Space>
          }
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreateDeposit}
          >
            <Form.Item
              name="partnerId"
              label="Đối tác"
              rules={[{ required: true, message: 'Vui lòng chọn đối tác' }]}
            >
              <Select
                showSearch
                placeholder="Chọn đối tác"
                onSearch={value => setSearchQuery(value)}
                filterOption={false}
                options={partners.map(partner => ({
                  value: partner.userId,
                  label: partner.name || partner.username
                }))}
              />
            </Form.Item>

            <Form.Item
              name="amount"
              label="Số tiền"
              rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Nhập số tiền"
                min={1}
              />
            </Form.Item>

            <Form.Item
              name="note"
              label="Ghi chú"
            >
              <TextArea
                rows={4}
                placeholder="Nhập ghi chú"
              />
            </Form.Item>
          </Form>
        </Drawer>

        <Modal
          title={
            <div style={{ textAlign: 'center' }}>
              {selectedDeposit?.newStatus === 1 ? 'Duyệt' : 'Từ chối'} yêu cầu nạp tiền
            </div>
          }
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={() => setModalVisible(false)}
          confirmLoading={loading}
          okText={selectedDeposit?.newStatus === 1 ? 'Xác nhận duyệt' : 'Xác nhận từ chối'}
          cancelText="Hủy"
        >
          <p>Bạn có chắc chắn muốn {selectedDeposit?.newStatus === 1 ? 'duyệt' : 'từ chối'} yêu cầu nạp tiền này?</p>
        </Modal>
      </div>
    </Container>
  );
};

export default AdminDeposit; 
